import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ShoppingBag, CreditCard, Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { publicApi } from '../context/AuthContext';

export default function PaymentPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled');
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await publicApi.get(`/payment/${token}`);
        setOrder(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.detail || 'Lien de paiement invalide ou expiré');
        setLoading(false);
      }
    };

    if (token) {
      fetchOrder();
    }
  }, [token]);

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const response = await publicApi.post(`/payment/${token}/pay`);
      // Rediriger vers Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création du paiement');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#00aaff] mx-auto mb-4" />
          <p className="text-gray-600">Chargement de votre commande...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Lien invalide</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a 
            href="/"
            className="inline-block bg-[#00aaff] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#0088cc] transition-colors"
          >
            Retour à l'accueil
          </a>
        </div>
      </div>
    );
  }

  if (order?.status === 'paid' || order?.payment_status === 'paid') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Commande déjà payée</h1>
          <p className="text-gray-600 mb-2">La commande #{order.order_number} a déjà été réglée.</p>
          <p className="text-gray-500 text-sm mb-6">Merci pour votre confiance !</p>
          <a 
            href="/"
            className="inline-block bg-[#00aaff] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#0088cc] transition-colors"
          >
            Retour à la boutique
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-gray-900 mb-2">
            <span className="text-[#00aaff]">Joey</span> Cyan
          </h1>
          <p className="text-gray-600">Paiement sécurisé</p>
        </div>

        {/* Cancelled Notice */}
        {cancelled && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="text-amber-600 flex-shrink-0" />
            <p className="text-amber-800">Paiement annulé. Vous pouvez réessayer quand vous le souhaitez.</p>
          </div>
        )}

        {/* Order Summary Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-[#00aaff] to-[#0077cc] p-6 text-white">
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-8 h-8" />
              <div>
                <p className="text-white/80 text-sm">Commande</p>
                <p className="text-xl font-bold">#{order?.order_number}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Customer Info */}
            <div className="mb-6 pb-6 border-b border-gray-100">
              <p className="text-sm text-gray-500 mb-1">Client</p>
              <p className="font-semibold text-gray-900">{order?.customer_name}</p>
              <p className="text-gray-600">{order?.customer_email}</p>
            </div>

            {/* Items */}
            <div className="mb-6">
              <p className="text-sm font-semibold text-gray-500 uppercase mb-3">Articles</p>
              <div className="space-y-3">
                {order?.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-900">
                        {item.name || item.product_name}
                        {item.color && <span className="text-gray-500 text-sm ml-2">({item.color})</span>}
                      </p>
                      <p className="text-sm text-gray-500">Qté: {item.quantity}</p>
                    </div>
                    <p className="font-semibold text-gray-900">{(item.price * item.quantity).toFixed(2)}€</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="border-t border-gray-100 pt-4 space-y-2">
              <div className="flex justify-between text-gray-600">
                <span>Sous-total</span>
                <span>{order?.subtotal_ttc?.toFixed(2)}€</span>
              </div>
              {order?.shipping_cost_ttc > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Livraison</span>
                  <span>{order?.shipping_cost_ttc?.toFixed(2)}€</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold text-gray-900 pt-2 border-t border-gray-200">
                <span>Total</span>
                <span className="text-[#00aaff]">{order?.total_ttc?.toFixed(2)}€</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Button */}
        <button
          onClick={handlePayment}
          disabled={processing}
          className="w-full bg-[#00aaff] hover:bg-[#0088cc] disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-colors shadow-lg"
        >
          {processing ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              Redirection vers le paiement...
            </>
          ) : (
            <>
              <CreditCard className="w-6 h-6" />
              Payer {order?.total_ttc?.toFixed(2)}€ par carte
            </>
          )}
        </button>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">
            🔒 Paiement sécurisé par Stripe. Vos informations bancaires ne sont jamais stockées sur nos serveurs.
          </p>
        </div>
      </div>
    </div>
  );
}
